const UNKNOWN_ARTIST_KEYS = new Set([
    "",
    "unknownartist",
    "youtubechannel",
    "youtubeartist",
    "artist",
    "unknown",
    "youtube",
    "vevo",
    "topic",
    "officialartistchannel",
]);

const YOUTUBE_LIKE_PLATFORMS = new Set([
    "youtube",
    "youtubemusic",
    "ytmusic",
    "yt",
]);

const YOUTUBE_TITLE_NOISE = /\s*(?:\(|\[|\{)(?:official(?:\s+music)?\s+video|official\s+audio|official\s+lyrics?|lyrics?|lyric\s+video|visualizer|audio|hd|hq|4k)\s*(?:\)|\]|\})/gi;
const YOUTUBE_TOPIC_SUFFIX = /\s*-\s*topic$/i;
const YOUTUBE_SHORTS_SUFFIX = /\s+#shorts\b/gi;
const YOUTUBE_ARTIST_NOISE_SUFFIX = /\s*-\s*(?:topic|vevo|official(?:\s+artist)?(?:\s+channel)?)$/i;
const DASH_VARIANTS_REGEX = /[\u2010\u2011\u2012\u2013\u2014\u2015]/g;
const DASH_MOJIBAKE_REGEX = /\u00e2\u20ac\u201c|\u00e2\u20ac\u201d/g;
const TITLE_HINT_REGEX = /\b(remix|mix|version|edit|live|acoustic|karaoke|cover|instrumental|ost|soundtrack|theme|lyrics?|lyric|visualizer|audio)\b/i;

const normalizeWhitespace = (value: string): string =>
    value.replace(/\s+/g, " ").trim();

const toSafeText = (value: unknown, fallback: string): string => {
    if (typeof value !== "string") return fallback;
    const normalized = normalizeWhitespace(value);
    return normalized || fallback;
};

const normalizeComparable = (value: string): string =>
    value.toLowerCase().replace(/[^a-z0-9]/g, "");

const normalizePlatformKey = (value: unknown): string =>
    String(value || "").toLowerCase().replace(/[^a-z0-9]/g, "");

const normalizeDashVariants = (value: string): string =>
    normalizeWhitespace(value.replace(DASH_VARIANTS_REGEX, "-").replace(DASH_MOJIBAKE_REGEX, "-"));

const normalizeArtistNoise = (value: string): string =>
    normalizeDashVariants(
        value
            .replace(YOUTUBE_TOPIC_SUFFIX, "")
            .replace(YOUTUBE_ARTIST_NOISE_SUFFIX, "")
    );

const escapeRegex = (value: string): string =>
    value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const keysLikelyMatch = (left: string, right: string): boolean => {
    if (!left || !right) return false;
    return (
        left === right ||
        left.includes(right) ||
        right.includes(left)
    );
};

const splitArtistAndTitle = (value: string): { artist: string; title: string } | null => {
    const normalizedValue = normalizeDashVariants(value);
    const separators = [" - ", "- ", " -", " | ", "| ", " |", ": "];
    for (const separator of separators) {
        const index = normalizedValue.indexOf(separator);
        if (index <= 0) continue;
        const left = normalizeWhitespace(normalizedValue.slice(0, index));
        const right = normalizeWhitespace(normalizedValue.slice(index + separator.length));
        if (left && right) {
            return { artist: left, title: right };
        }
    }
    return null;
};

const stripYouTubeTitleNoise = (title: string): string =>
    normalizeDashVariants(title.replace(YOUTUBE_TITLE_NOISE, "").replace(YOUTUBE_SHORTS_SUFFIX, ""));

const hasTitleHints = (value: string): boolean =>
    TITLE_HINT_REGEX.test(value) || /\(|\[|\{/.test(value);

const isLikelyArtistSegment = (value: string): boolean => {
    const segment = normalizeWhitespace(value);
    if (!segment) return false;
    if (hasTitleHints(segment)) return false;
    return segment.split(" ").length <= 6;
};

const inferOrientation = (parsed: { artist: string; title: string }): "artist-title" | "title-artist" => {
    const leftArtistLike = isLikelyArtistSegment(parsed.artist);
    const rightArtistLike = isLikelyArtistSegment(parsed.title);
    const leftTitleHints = hasTitleHints(parsed.artist);
    const rightTitleHints = hasTitleHints(parsed.title);

    if (leftArtistLike && !rightArtistLike) return "artist-title";
    if (rightArtistLike && !leftArtistLike) return "title-artist";
    if (rightTitleHints && !leftTitleHints) return "artist-title";
    if (leftTitleHints && !rightTitleHints) return "title-artist";

    const leftWords = parsed.artist.split(" ").length;
    const rightWords = parsed.title.split(" ").length;
    if (leftWords < rightWords) return "artist-title";
    if (rightWords < leftWords) return "title-artist";

    return parsed.artist.length <= parsed.title.length ? "artist-title" : "title-artist";
};

export const getReadableSongMetadata = (
    titleInput: unknown,
    artistInput: unknown,
    platformInput?: unknown,
): { title: string; artist: string } => {
    const platform = normalizePlatformKey(platformInput);
    const isYouTubeLike =
        YOUTUBE_LIKE_PLATFORMS.has(platform) || platform.includes("youtube");

    let title = normalizeDashVariants(toSafeText(titleInput, "Untitled"));
    let artist = normalizeArtistNoise(toSafeText(artistInput, "Unknown Artist"));
    let artistIsUnknown = UNKNOWN_ARTIST_KEYS.has(normalizeComparable(artist));

    if (isYouTubeLike || artistIsUnknown) {
        title = stripYouTubeTitleNoise(title);
        artist = normalizeArtistNoise(artist);
        artistIsUnknown = UNKNOWN_ARTIST_KEYS.has(normalizeComparable(artist));
    }

    const parsedTitle = splitArtistAndTitle(title);
    if (parsedTitle) {
        const artistKey = normalizeComparable(artist);
        const leftKey = normalizeComparable(parsedTitle.artist);
        const rightKey = normalizeComparable(parsedTitle.title);

        const leftMatchesArtist = keysLikelyMatch(leftKey, artistKey);
        const rightMatchesArtist = keysLikelyMatch(rightKey, artistKey);

        if (artistKey && leftMatchesArtist && !rightMatchesArtist) {
            artist = parsedTitle.artist;
            title = parsedTitle.title;
        } else if (artistKey && rightMatchesArtist && !leftMatchesArtist) {
            title = parsedTitle.artist;
            artist = parsedTitle.title;
        } else if (artistIsUnknown || isYouTubeLike) {
            const orientation = inferOrientation(parsedTitle);
            if (orientation === "artist-title") {
                artist = parsedTitle.artist;
                title = parsedTitle.title;
            } else {
                artist = parsedTitle.title;
                title = parsedTitle.artist;
            }
        } else if (artistKey) {
            const normalizedArtistValue = normalizeWhitespace(artist);
            const lowerTitle = title.toLowerCase();
            const lowerArtist = normalizedArtistValue.toLowerCase();
            if (lowerArtist && lowerTitle.includes(lowerArtist)) {
                if (parsedTitle.artist.toLowerCase().includes(lowerArtist)) {
                    title = parsedTitle.title;
                } else if (parsedTitle.title.toLowerCase().includes(lowerArtist)) {
                    title = parsedTitle.artist;
                }
            }
        }
    }

    const parsedArtist = splitArtistAndTitle(artist);
    if (parsedArtist) {
        const currentTitleKey = normalizeComparable(title);
        const parsedArtistLeftKey = normalizeComparable(parsedArtist.artist);
        const parsedArtistRightKey = normalizeComparable(parsedArtist.title);
        const leftMatchesTitle = keysLikelyMatch(parsedArtistLeftKey, currentTitleKey);
        const rightMatchesTitle = keysLikelyMatch(parsedArtistRightKey, currentTitleKey);

        if ((leftMatchesTitle && !rightMatchesTitle) || title.toLowerCase() === "untitled") {
            artist = parsedArtist.title;
            if (title.toLowerCase() === "untitled") {
                title = parsedArtist.artist;
            }
        } else if (rightMatchesTitle && !leftMatchesTitle) {
            artist = parsedArtist.artist;
        }
    }

    const normalizedArtist = normalizeWhitespace(artist);
    if (normalizedArtist && normalizedArtist.toLowerCase() !== "unknown artist") {
        const escapedArtist = escapeRegex(normalizedArtist);
        title = normalizeWhitespace(
            title
                .replace(new RegExp(`^${escapedArtist}\\s*[-|:]\\s*`, "i"), "")
                .replace(new RegExp(`\\s*[-|:]\\s*${escapedArtist}$`, "i"), "")
        ) || title;
    }

    title = normalizeWhitespace(title) || "Untitled";
    artist = normalizeArtistNoise(artist) || "Unknown Artist";

    return { title, artist };
};
